import { useTranslation } from "react-i18next";
import { useLocation, useRouteLoaderData, useSubmit } from "@remix-run/react";
import { type SupportedLanguages } from "~/i18n";
import { useRootLoaderData } from "../apis/utils";
import { useCallback, useRef, useState } from "react"
import { withDialogHoC, type DialogProps, type DialogRefType, type DialogResponseType } from "./common/withDialogHoC";
import type { CurrencyType } from "@client/api-services/client";
import { useUrlGenerator } from "~/apis/image-path";
import { LANGUAGE_NAMES, SUPPORTED_LANGUAGES } from "@elemt/app-i18n";

declare type UserPrefProps = { supportedCurrencies: CurrencyType[], prefs: { currencyCode: string, locale: SupportedLanguages } }
declare type UserPrefsReturnType = { locale: SupportedLanguages, currencyCode: string }
export function UserPrefsDialog({ isOpen, close, data }: DialogProps<UserPrefProps, UserPrefsReturnType>) {
	const [locale, setLocale] = useState(() => data.prefs.locale);
	const { t } = useTranslation();
	const [currencyCode, setCurrencyCode] = useState(() => data.prefs.currencyCode);
	return (
		<div className="flex-1 grid grid-cols-2 items-center gap-4 text-sm p-4">
			<label>{t("common.language")}:</label>
			<div>
				<select className="form-control text-sm" value={locale} onChange={e => setLocale(e.target.value as SupportedLanguages)}>
					{LANGUAGE_NAMES.map(ln => <option key={ln.key} value={ln.key}>{ln.value}</option>)}
				</select>
			</div>
			<label>{t("common.billing_currency")}:</label>
			<div>
				<select className="form-control text-sm" value={currencyCode} onChange={e => setCurrencyCode(e.target.value)}>
					{data.supportedCurrencies.map(c => <option key={c.currencyCode} value={c.currencyCode}>{c.currencyName}</option>)}
				</select>
			</div>
			<div className="col-span-2 flex gap-2 justify-end">
				<button onClick={() => close({ isCancelled: true })}
					className="btn-light-sm">{t("common.cancel")}</button>
				<button onClick={() => close({ isCancelled: false, value: { locale, currencyCode } })}
					className="btn-dark-sm">{t("common.save")}</button>
			</div>
		</div>
	)
}
const UserPrefsDialogHoC = withDialogHoC(UserPrefsDialog, { closeOnEscape: true });

export function UserPrefsComponent() {
	const { i18n } = useTranslation();
	const submit = useSubmit();
	const dialogRef = useRef<DialogRefType<UserPrefProps>>(null);
	const { supportedCurrencies, userPrefs } = useRootLoaderData();
	const { getCountryImageUrl } = useUrlGenerator();
	const countryCode = userPrefs.chosenCountryCode ?? userPrefs.detectedCountryCode;
	const currencyCode = userPrefs.chosenCurrencyCode ?? userPrefs.detectedCurrencyCode;
	const locale = i18n.language as SupportedLanguages;
	const location = useLocation();
	const currentUrl = `${location.pathname}${location.search}`;

	const openPrefDialog = useCallback(() => {
		dialogRef.current?.openDialog({ supportedCurrencies, prefs: { currencyCode, locale } });
	}, [currencyCode, locale, supportedCurrencies])

	const onPrefChanged = useCallback(async (response: DialogResponseType<UserPrefsReturnType>) => {
		if (response.isCancelled) return;
		submit(response.value, { action: `/apis/action/change-prefs?pageUrl=${currentUrl}`, method: "POST" })
	}, [submit, currentUrl]);
	return <>
		<button onClick={openPrefDialog} className="inline-flex items-center gap-1 user-prefs-item">
			<img src={getCountryImageUrl(countryCode, "4x3")} className="w-6" />
			<span className="mx-1">|</span>
			<span className="uppercase">{locale}</span>
			<span className="mx-1">|</span>
			<span className="uppercase">{currencyCode}</span>
		</button>
		<UserPrefsDialogHoC ref={dialogRef} width={500} callback={onPrefChanged} />
	</>
}